import { createServerFn } from "@tanstack/react-start";

import {
  type CommunityDetailsInput,
  type CommunityLinkCreateInput,
  communityLinkCreateInputSchema,
  type CommunityLinkDeleteInput,
  communityLinkDeleteInputSchema,
  type CommunityLinkUpdateInput,
  communityLinkUpdateInputSchema,
  communityDetailsInputSchema,
  type CommunityListInput,
  communityListInputSchema,
  communitySearchInputSchema,
  type CommunitySearchBrowserInput,
  type CommunitySetHiddenInput,
  communitySetHiddenInputSchema,
} from "@/api/contracts/communities";
import { assertCapability } from "@/api/functions/admin-gate";
import { requireAuth } from "@/api/middleware/auth";
import type { ResolvedSession } from "@/server/use-cases/auth/resolve-session";

// The browser's doors onto the community reads: the community search (#708),
// the Community ID field's options searched by full id or name, the
// communities list (#744), one page of the communities page, and one
// community's details and map data (#745), the community page's and every
// Homesite map's.
//
// Three gates on each, the visible-divisions read's three (`@/api/functions/divisions`):
// `requireAuth`; `canBrowseSpecs`, because Python fronts this read with the
// same `SPEC_READ_ROLES` its Spec list reads use — the capability is what
// mirrors that set (`@/lib/spec-read-access-drift.unit.test.ts`), so deleting
// this check would change no outcome, only the error; and Python's gate and
// use case behind it, the enforcement. Python fronts the list and the two
// one-community reads with the same set, so they take the same capability
// rather than a new one.
//
// Four writes, hide/unhide (#752) and the three community link verbs (#754),
// each gated on `canEditSpecs` — the source's
// `isSpecsEditor`, which is modsy's `canToggleVisibility` group check exactly,
// and Python's `COMMUNITY_WRITE_ROLES` exactly
// (`@/lib/community-write-access-drift.unit.test.ts`). Necessary, not
// sufficient, as on the spec verbs: a Division Editor's community scope
// (ADR-0016 decision 9) is Python's, which also stays the enforcement.
//
// Error scrubbing is global function middleware (`start.ts`). Server-only
// modules are pulled in with a dynamic `import()` inside the handler.

interface HandlerContext {
  session: ResolvedSession;
}

async function handleCommunitySearch({
  data,
  context,
}: {
  data: CommunitySearchBrowserInput;
  context: HandlerContext;
}) {
  assertCapability(context.session, "canBrowseSpecs", "browse", "communities");
  const { searchInternalCommunities } = await import("@/server/data/internal-api/communities");
  // Straight through: the declaration's encoder owns which keys reach the wire.
  return searchInternalCommunities(data, { sessionId: context.session.sessionId });
}

async function handleCommunityList({
  data,
  context,
}: {
  data: CommunityListInput;
  context: HandlerContext;
}) {
  assertCapability(context.session, "canBrowseSpecs", "browse", "communities");
  const { listInternalCommunities } = await import("@/server/data/internal-api/communities");
  // Straight through, the term raw: Python processes it once.
  return listInternalCommunities(data, { sessionId: context.session.sessionId });
}

async function handleCommunityDetails({
  data,
  context,
}: {
  data: CommunityDetailsInput;
  context: HandlerContext;
}) {
  assertCapability(context.session, "canBrowseSpecs", "browse", "communities");
  const { getInternalCommunityDetails } = await import("@/server/data/internal-api/communities");
  // `null` for an absent community: the page's 404 state.
  return getInternalCommunityDetails(data, { sessionId: context.session.sessionId });
}

async function handleCommunityMapData({
  data,
  context,
}: {
  data: CommunityDetailsInput;
  context: HandlerContext;
}) {
  assertCapability(context.session, "canBrowseSpecs", "browse", "communities");
  const { getInternalCommunityMapData } = await import("@/server/data/internal-api/communities");
  return getInternalCommunityMapData(data, { sessionId: context.session.sessionId });
}

async function handleCommunitySetHidden({
  data,
  context,
}: {
  data: CommunitySetHiddenInput;
  context: HandlerContext;
}) {
  assertCapability(context.session, "canEditSpecs", "change", "communities");
  const { setInternalCommunityHidden } = await import("@/server/data/internal-api/communities");
  // Straight through: the corrected page, the list read's own answer.
  return setInternalCommunityHidden(data, { sessionId: context.session.sessionId });
}

async function handleCommunityLinkCreate({
  data,
  context,
}: {
  data: CommunityLinkCreateInput;
  context: HandlerContext;
}) {
  assertCapability(context.session, "canEditSpecs", "change", "communities");
  const { createInternalCommunityLink } = await import("@/server/data/internal-api/communities");
  // Straight through, the URL already defaulted by the validator: the
  // community's links, read inside the write's own transaction.
  return createInternalCommunityLink(data, { sessionId: context.session.sessionId });
}

async function handleCommunityLinkUpdate({
  data,
  context,
}: {
  data: CommunityLinkUpdateInput;
  context: HandlerContext;
}) {
  assertCapability(context.session, "canEditSpecs", "change", "communities");
  const { updateInternalCommunityLink } = await import("@/server/data/internal-api/communities");
  return updateInternalCommunityLink(data, { sessionId: context.session.sessionId });
}

async function handleCommunityLinkDelete({
  data,
  context,
}: {
  data: CommunityLinkDeleteInput;
  context: HandlerContext;
}) {
  assertCapability(context.session, "canEditSpecs", "change", "communities");
  const { deleteInternalCommunityLink } = await import("@/server/data/internal-api/communities");
  return deleteInternalCommunityLink(data, { sessionId: context.session.sessionId });
}

/**
 * The handler bodies, exported for unit tests: a `createServerFn` value is not
 * callable as a plain function outside the Start runtime.
 */
export const __test_handlers = {
  handleCommunitySearch,
  handleCommunityList,
  handleCommunityDetails,
  handleCommunityMapData,
  handleCommunitySetHidden,
  handleCommunityLinkCreate,
  handleCommunityLinkUpdate,
  handleCommunityLinkDelete,
};

export const communitySearch = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(communitySearchInputSchema)
  .handler(handleCommunitySearch);

export const communityList = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(communityListInputSchema)
  .handler(handleCommunityList);

export const communityDetails = createServerFn({ method: "GET" })
  .middleware([requireAuth])
  .validator(communityDetailsInputSchema)
  .handler(handleCommunityDetails);

export const communityMapData = createServerFn({ method: "GET" })
  .middleware([requireAuth])
  .validator(communityDetailsInputSchema)
  .handler(handleCommunityMapData);

export const communitySetHidden = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(communitySetHiddenInputSchema)
  .handler(handleCommunitySetHidden);

export const communityLinkCreate = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(communityLinkCreateInputSchema)
  .handler(handleCommunityLinkCreate);

export const communityLinkUpdate = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(communityLinkUpdateInputSchema)
  .handler(handleCommunityLinkUpdate);

export const communityLinkDelete = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(communityLinkDeleteInputSchema)
  .handler(handleCommunityLinkDelete);
